import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { AlertCircle, RefreshCcw } from "lucide-react";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AnimatedTabs } from "@/components/ui/animated-tabs";
import { Button } from "@/components/ui/button";
import { useStudySetParams } from "@/lib/hooks/use-studyset-params";
import { SubjectArray } from "@/lib/types";
import SubjectListSliderSkeleton from "./subject-list-slider-skeleton";

type Props = {
  overlay?: boolean;
  label?: string;
};

export const SubjectListSlider = () => {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          fallbackRender={({ resetErrorBoundary }) => (
            <div className="relative w-full">
              <div className="w-full border-b border-gray-200">
                <div className="flex space-x-2 px-2 py-4">
                  <Alert variant="destructive" className="w-full">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Failed to load subjects</AlertTitle>
                    <AlertDescription className="flex items-center justify-between">
                      <span>
                        There was a problem loading the subject list. Please try
                        again.
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => resetErrorBoundary()}
                        className="ml-4"
                      >
                        <RefreshCcw className="mr-2 h-4 w-4" />
                        Retry
                      </Button>
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            </div>
          )}
        >
          <Suspense fallback={<SubjectListSliderSkeleton />}>
            <SubjectListSliderSuspense />
          </Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
};

export const SubjectListSliderSuspense = ({ overlay, label }: Props) => {
  // const trpc = useTRPC();
  // const { data: subjects } = useSuspenseQuery(
  //   trpc.studysets.getSubjects.queryOptions(),
  // );
  const { setParams, subject: currentSubject } = useStudySetParams();

  const handleSubjectChange = (subject: string) => {
    setParams({
      subject,
    });
  };

  const allSubjects = ["all", ...SubjectArray];

  const tabs = allSubjects.map((subject) => ({
    id: subject,
    label: subject === "all" ? "All Subjects" : subject,
  }));

  return (
    <div className="w-full">
      {label && <div className="pl-7 mb-3 text-muted-foreground">{label}</div>}
      <AnimatedTabs
        tabs={tabs}
        activeTab={currentSubject}
        onTabChange={handleSubjectChange}
        overlay={overlay}
      />
    </div>
  );
};
